import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { scrollToId } from "./SmoothScroll";
import { LangToggle, useT } from "../lib/i18n";

const Item = ({ to, onClick, testid, children }) => {
  const cls = "block py-2 text-2xl sm:text-3xl font-display leading-none text-white/90 hover:text-[#1FD1FF] hover:translate-x-1 transition-[color,transform]";
  return to
    ? <Link to={to} data-testid={testid} className={cls}>{children}</Link>
    : <button onClick={onClick} data-testid={testid} className={`${cls} text-left`}>{children}</button>;
};

const Group = ({ title, children, testid }) => (
  <div data-testid={testid}>
    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1FD1FF] mb-3">{title}</p>
    <div>{children}</div>
  </div>
);

export const MenuSheet = ({ open, onClose }) => {
  const { user, openAuth, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const [genres, setGenres] = useState([]);

  useEffect(() => { onClose(); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || genres.length) return;
    api.get("/genres").then((r) => setGenres(Array.isArray(r.data) ? r.data.slice(0, 8) : [])).catch(() => {});
  }, [open, genres.length]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (id) => {
    onClose();
    if (loc.pathname !== "/") { nav("/"); setTimeout(() => scrollToId(id), 450); } else setTimeout(() => scrollToId(id), 50);
  };
  const auth = (mode) => { onClose(); openAuth(mode); };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div data-testid="menu-sheet" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[70] bg-[#04070D] overflow-y-auto" data-lenis-prevent>
          <motion.div initial={{ x: -30 }} animate={{ x: 0 }} exit={{ x: -30 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-[1500px] mx-auto px-5 lg:px-10 pb-16">
            <div className="h-16 flex items-center justify-between">
              <button data-testid="menu-close-button" onClick={onClose} aria-label={t("menu.close")} className="p-2 -ml-2 text-white hover:text-[#1FD1FF]"><X className="w-7 h-7" /></button>
              <LangToggle />
            </div>
            <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4 mt-6">
              <Group title={t("menu.music")} testid="menu-group-music">
                <Item to="/" testid="menu-home">{t("menu.home")}</Item>
                <Item to="/nuevos" testid="menu-new">{t("browse.new")}</Item>
                <Item to="/populares" testid="menu-popular">{t("browse.popular")}</Item>
                <Item onClick={() => go("top")} testid="menu-top">{t("top.title")}</Item>
                <Item to="/generos" testid="menu-genres">{t("menu.genres")}</Item>
                <Item to="/favoritos" testid="menu-favorites">{t("nav.favs")}</Item>
              </Group>
              <Group title={t("menu.dj")} testid="menu-group-dj">
                <Item onClick={() => go("mixes")} testid="menu-mixes">{t("nav.mixes")}</Item>
                <Item onClick={() => go("tools")} testid="menu-tools">{t("nav.tools")}</Item>
                <Item onClick={() => go("fx-deck")} testid="menu-fx">{t("nav.fx")}</Item>
              </Group>
              <Group title={t("menu.genres")} testid="menu-group-genres">
                {genres.map((g) => <Item key={g.name} to={`/generos/${encodeURIComponent(g.name)}`} testid={`menu-genre-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{g.name}</Item>)}
              </Group>
              <Group title={t("menu.more")} testid="menu-group-more">
                {user ? (
                  <>
                    {user.role === "admin" && <Item to="/admin" testid="menu-studio">{t("nav.studio")}</Item>}
                    <Item onClick={() => { onClose(); logout(); }} testid="menu-logout">{t("menu.logout")}</Item>
                  </>
                ) : (
                  <>
                    <Item onClick={() => auth("login")} testid="menu-login">{t("nav.login")}</Item>
                    <Item onClick={() => auth("signup")} testid="menu-signup">{t("menu.signup")}</Item>
                  </>
                )}
                <Item onClick={() => go("contacto")} testid="menu-socials">{t("menu.socials")}</Item>
              </Group>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
